import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, UserCircle2, Bell, Loader2, Eye, EyeOff, ImageIcon, Palette, Lock } from 'lucide-react';
import PlayerCleanupCard from '@/components/settings/PlayerCleanupCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, checkAuth, logout } = useAuth();
  const isPro = (user?.subscription || 'free').startsWith('pro_');

  // Profile state
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [organizationLogo, setOrganizationLogo] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [brandAccentColor, setBrandAccentColor] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const logoInputRef = useRef(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    setName(user?.name || '');
    setOrganization(user?.organization || '');
    setOrganizationLogo(user?.organization_logo || '');
    setEmailNotifications(user?.email_notifications !== false);
    setBrandAccentColor(user?.brand_accent_color || '');
  }, [user, isAuthenticated, isLoading, navigate]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('session_token');
      // Only include brand_accent_color in the payload when the user is Pro
      // AND actually changed it — non-Pro sends would 403 from the server
      // and drop the rest of the profile update.
      const payload = {
        name: name.trim(),
        organization: organization.trim(),
        organization_logo: organizationLogo,
        email_notifications: emailNotifications,
      };
      if (isPro && (brandAccentColor || user?.brand_accent_color)) {
        payload.brand_accent_color = brandAccentColor.trim();
      }
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      toast.success('Profile updated');
      await checkAuth();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (PNG, JPG, GIF, WebP)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    setLogoUploading(true);
    try {
      const token = localStorage.getItem('session_token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/upload/organization-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setOrganizationLogo(data.storage_path);
      toast.success('Logo uploaded — saved to your profile');
      await checkAuth();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!organizationLogo) return;
    if (!window.confirm('Remove the organization logo from every shared schedule and leaderboard?')) return;
    setOrganizationLogo('');
    // Persist the removal immediately so the user doesn't have to also click "Save Profile".
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ organization_logo: '' }),
      });
      toast.success('Organization logo removed');
      await checkAuth();
    } catch {
      toast.error('Could not remove logo');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Password change failed');
      toast.success('Password updated. Other devices have been logged out.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || 'Password change failed');
    } finally {
      setSavingPassword(false);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="settings-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
            ACCOUNT SETTINGS
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Profile */}
        <Card className="border-slate-200" data-testid="settings-profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserCircle2 className="w-5 h-5 text-[#00247D]" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSaveProfile}>
              <div>
                <Label htmlFor="settings-email">Email</Label>
                <Input id="settings-email" value={user.email} disabled className="mt-1 bg-slate-100" data-testid="settings-email-input" />
                <p className="text-xs text-slate-400 mt-1">Email changes are coming soon. Contact support if you need to update yours now.</p>
              </div>
              <div>
                <Label htmlFor="settings-name">Full Name</Label>
                <Input id="settings-name" value={name} onChange={(e) => setName(e.target.value)}
                  required maxLength={100} className="mt-1" data-testid="settings-name-input" />
              </div>
              <div>
                <Label htmlFor="settings-org">Organization</Label>
                <div className="mt-1 flex items-start gap-3" data-testid="settings-org-row">
                  {/* Logo thumb + upload control, sitting BESIDE the org name input
                      so they read as one combined "Organization" field. */}
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                    className="shrink-0 w-12 h-12 rounded-md bg-slate-50 border border-slate-200 hover:border-[#00247D] flex items-center justify-center overflow-hidden transition-colors group"
                    title={organizationLogo ? 'Replace organization logo' : 'Upload organization logo'}
                    data-testid="settings-org-logo-thumb"
                  >
                    {organizationLogo ? (
                      <img
                        src={`${API_URL}/api/files/${organizationLogo}`}
                        alt="Organization logo preview"
                        className="w-full h-full object-contain"
                        data-testid="settings-org-logo-preview"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : logoUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#00247D]" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-slate-300 group-hover:text-[#00247D]" />
                    )}
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="settings-org-logo-input"
                  />
                  <div className="flex-1 min-w-0">
                    <Input id="settings-org" value={organization} onChange={(e) => setOrganization(e.target.value)}
                      placeholder="e.g., Cayman Basketball League" maxLength={120}
                      data-testid="settings-org-input" />
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="text-xs text-slate-400">
                        Name + logo appear together on every shared schedule, public team page, and leaderboard you publish.
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={logoUploading}
                          className="text-xs font-semibold text-[#00247D] hover:underline underline-offset-2 disabled:opacity-40"
                          data-testid="settings-org-logo-upload-btn">
                          {logoUploading ? 'Uploading…' : organizationLogo ? 'Replace logo' : 'Upload logo'}
                        </button>
                        {organizationLogo && (
                          <>
                            <span className="text-slate-300">·</span>
                            <button
                              type="button"
                              onClick={handleRemoveLogo}
                              className="text-xs font-semibold text-rose-500 hover:underline underline-offset-2"
                              data-testid="settings-org-logo-remove-btn">
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-100">
                <div className="flex items-start gap-3 min-w-0">
                  <Palette className="w-4 h-4 mt-1 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <Label htmlFor="settings-brand-color" className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      Brand accent color
                      {!isPro && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid="brand-color-pro-badge">
                          <Lock className="w-2.5 h-2.5" /> Pro
                        </span>
                      )}
                    </Label>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Replaces the amber accent on your share graphics — pill outlines, countdown text, and diagonal stripe pick up this color.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="color"
                    id="settings-brand-color"
                    value={brandAccentColor || '#F59E0B'}
                    onChange={(e) => setBrandAccentColor(e.target.value)}
                    disabled={!isPro}
                    className="h-9 w-12 rounded border border-slate-200 bg-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="settings-brand-color-picker"
                    title={isPro ? 'Choose your brand color' : 'Upgrade to Pro to customize'}
                  />
                  <Input
                    aria-label="Brand accent color hex code"
                    value={brandAccentColor}
                    onChange={(e) => setBrandAccentColor(e.target.value)}
                    placeholder="#F59E0B"
                    disabled={!isPro}
                    maxLength={7}
                    className="w-24 font-mono text-xs"
                    data-testid="settings-brand-color-hex"
                  />
                  {isPro && brandAccentColor && (
                    <button
                      type="button"
                      onClick={() => setBrandAccentColor('')}
                      className="text-xs font-semibold text-slate-400 hover:text-rose-500"
                      data-testid="settings-brand-color-reset">
                      Reset
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-100">
                <div className="flex items-start gap-3">
                  <Bell className="w-4 h-4 mt-1 text-slate-500" />
                  <div>
                    <Label htmlFor="settings-notifs" className="text-sm font-medium">Email notifications</Label>
                    <p className="text-xs text-slate-400 mt-0.5">Receive game reminders, roster prompts, and platform announcements.</p>
                  </div>
                </div>
                <Switch
                  id="settings-notifs"
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                  data-testid="settings-notifs-switch"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={savingProfile} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="settings-save-profile-btn">
                  {savingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Player Cleanup (Suggested Merges) */}
        <PlayerCleanupCard />

        {/* Change Password */}
        <Card className="border-slate-200" data-testid="settings-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="w-5 h-5 text-[#00247D]" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleChangePassword}>
              <div>
                <Label htmlFor="settings-current-pw">Current Password</Label>
                <div className="relative mt-1">
                  <Input id="settings-current-pw" type={showCurrent ? 'text' : 'password'}
                    value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                    required autoComplete="current-password" data-testid="settings-current-password" />
                  <button type="button" onClick={() => setShowCurrent(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="settings-new-pw">New Password</Label>
                <div className="relative mt-1">
                  <Input id="settings-new-pw" type={showNew ? 'text' : 'password'}
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    required minLength={6} autoComplete="new-password" data-testid="settings-new-password" />
                  <button type="button" onClick={() => setShowNew(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">At least 6 characters.</p>
              </div>
              <div>
                <Label htmlFor="settings-confirm-pw">Confirm New Password</Label>
                <Input id="settings-confirm-pw" type={showNew ? 'text' : 'password'}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  required minLength={6} autoComplete="new-password"
                  className="mt-1" data-testid="settings-confirm-password" />
              </div>
              <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Heads up:</strong> Changing your password will sign you out of every other device but keep this one logged in.
              </p>
              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="settings-save-password-btn">
                  {savingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : 'Update Password'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="border-red-200">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-800">Sign out of this device</div>
              <p className="text-xs text-slate-500 mt-0.5">You can sign back in with your email and password.</p>
            </div>
            <Button variant="outline" onClick={logout} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="settings-logout-btn">
              Log Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
